import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Layers,
  Search,
  ChevronDown,
  Check,
  ArrowRight,
  Info,
  X,
  Heart,
  Sparkles,
  AlertCircle,
} from 'lucide-react';
import { BRANDS, markers as allMarkers } from '../utils/data';
import { getUserCollection } from '../utils/userCollection';
import { decodePaletteFromShare } from '../utils/paletteCode';
import { findShades, classifyShadeSource, usesOldNewCodes } from '../utils/color';
import { useAuth } from '../contexts/AuthContext';
import { getUserPalettes, getPaletteById } from '../config/savedPalettes';
import SavePaletteModal from '../components/SavePaletteModal';
import PageHelpModal from '../components/PageHelpModal';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTextColor(hex) {
  const clean = (hex || '').replace('#', '');
  if (clean.length < 6) return '#1e293b';
  const r = parseInt(clean.substr(0, 2), 16);
  const g = parseInt(clean.substr(2, 2), 16);
  const b = parseInt(clean.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.5 ? '#1e293b' : '#ffffff';
}

function markerCodes(m) {
  if (!m) return [];
  if (usesOldNewCodes(m.brand)) {
    return [m.code_new, m.code_old].filter((c) => c && c !== '-');
  }
  return [m.code].filter((c) => c && c !== '-');
}

function markerKey(m, idx = 0) {
  if (!m) return `k-${idx}`;
  return `${m.brand}-${m.hex}-${(m.code || m.code_new || m.code_old || '').toString()}-${idx}`;
}

const QUALITY_STYLES = {
  excellent: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  good: 'bg-amber-50 text-amber-600 border-amber-200',
  approximate: 'bg-slate-100 text-slate-500 border-slate-200',
};
const QUALITY_LABELS = {
  excellent: 'Excellent',
  good: 'Good',
  approximate: 'Approximate',
};

// ─── Custom Select (matches HexFinder/Brand Converter) ───────────────────────

function CustomSelect({ value, onChange, options, getLabel }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const label = getLabel ? getLabel(value) : value;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-1.5 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer bg-white/80 backdrop-blur-sm border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          size={13}
          className={`flex-shrink-0 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 mt-2 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-xl border border-slate-100 py-2 z-50 animate-fadeIn max-h-60 overflow-y-auto">
          {options.map((opt) => {
            const optLabel = getLabel ? getLabel(opt) : opt;
            const selected = value === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
                className={`w-full px-4 py-1.5 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors ${
                  selected ? 'text-violet-600 font-semibold' : 'text-slate-700'
                }`}
              >
                <span className="truncate">{optLabel}</span>
                {selected && <Check size={12} className="text-violet-500 flex-shrink-0 ml-2" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Swatch (used in chains) ──────────────────────────────────────────────────

function ShadeSwatch({ marker, badge, role, isSource = false, sizeClass = 'w-20 h-20' }) {
  if (!marker) return null;
  const codes = markerCodes(marker);
  const textColor = getTextColor(marker.hex);
  return (
    <div className="flex flex-col items-center text-center min-w-0">
      <div
        className={`${sizeClass} rounded-xl shadow-sm relative shrink-0`}
        style={{ backgroundColor: marker.hex }}
      >
        <div className="absolute bottom-1.5 left-1.5 flex flex-col items-start gap-0.5 max-w-full">
          {codes.slice(0, 2).map((c, i) => (
            <span
              key={i}
              className="text-[10px] font-mono font-semibold leading-none tracking-tight"
              style={{ color: textColor }}
            >
              {c}
            </span>
          ))}
        </div>
        {badge && (
          <span
            className={`absolute -top-2 -right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${badge.style}`}
          >
            {badge.label}
          </span>
        )}
      </div>
      <p className={`text-[10px] font-semibold uppercase tracking-[0.2em] mt-2 truncate max-w-[90px] ${isSource ? 'text-fuchsia-500' : 'text-slate-400'}`}>
        {role}
      </p>
      <p className="text-[11px] text-slate-600 truncate max-w-[90px] font-medium">{marker.brand}</p>
    </div>
  );
}

function ShadeChain({ source, chain, compact = false }) {
  const sizeClass = compact ? 'w-14 h-14' : 'w-20 h-20';
  // Height of the ΔL label + its mt-1 gap below each shadow swatch, so the
  // source column has the same total height and all swatches top-align cleanly.
  const deltaLSpacer = !compact ? <div className="h-[18px]" /> : null;
  return (
    <div className="flex items-start gap-1 sm:gap-3 flex-wrap">
      <div className="flex flex-col items-center">
        <ShadeSwatch marker={source} role="Source" isSource sizeClass={sizeClass} />
        {deltaLSpacer}
      </div>
      {chain.map((step, i) => (
        <React.Fragment key={i}>
          {/* Arrow sits at the vertical midpoint of the swatch */}
          <div className="flex items-start" style={{ paddingTop: compact ? '24px' : '32px' }}>
            <ArrowRight size={14} className="text-slate-300 shrink-0" />
          </div>
          <div className="flex flex-col items-center">
            <ShadeSwatch
              marker={step.marker}
              role={`Shadow ${i + 1}`}
              sizeClass={sizeClass}
              badge={{
                label: QUALITY_LABELS[step.quality],
                style: QUALITY_STYLES[step.quality],
              }}
            />
            {!compact && (
              <span className="text-[9px] text-slate-400 mt-1">
                ΔL {step.metrics.lightDiff.toFixed(0)}
              </span>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Marker search dropdown for single mode ──────────────────────────────────

function MarkerSearch({ brand, codeType, onSelect, query, setQuery }) {
  const matches = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.toLowerCase().trim();
    let pool = allMarkers.filter((m) => m.brand === brand);
    if (usesOldNewCodes(brand) && codeType) {
      pool = pool.filter((m) =>
        codeType === 'old' ? m.code_old && m.code_old !== '-' : m.code_new && m.code_new !== '-'
      );
    }
    return pool
      .filter((m) => {
        if (m.name && m.name.toLowerCase().includes(q)) return true;
        if (usesOldNewCodes(brand)) {
          if (codeType === 'old' && m.code_old?.toLowerCase().includes(q)) return true;
          if (codeType === 'new' && m.code_new?.toLowerCase().includes(q)) return true;
          if (!codeType) {
            if (m.code_old?.toLowerCase().includes(q)) return true;
            if (m.code_new?.toLowerCase().includes(q)) return true;
          }
        } else if (m.code?.toLowerCase().includes(q)) {
          return true;
        }
        return false;
      })
      .slice(0, 30);
  }, [brand, codeType, query]);

  if (!query.trim()) return null;

  return (
    <div className="max-h-64 overflow-y-auto space-y-1 rounded-2xl border border-slate-200 p-2 bg-white shadow-lg">
      {matches.length === 0 ? (
        <p className="text-center text-slate-500 py-3 text-xs">No markers found.</p>
      ) : (
        matches.map((m, i) => {
          const codes = markerCodes(m);
          return (
            <button
              key={markerKey(m, i)}
              onClick={() => onSelect(m)}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
            >
              <div
                className="w-9 h-9 rounded-lg border-2 border-white shadow flex-shrink-0"
                style={{ backgroundColor: m.hex }}
              />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 text-sm truncate">{m.name || codes[0]}</p>
                <p className="text-[11px] text-slate-500 truncate">
                  {codes.join(' / ') || m.hex}
                </p>
              </div>
            </button>
          );
        })
      )}
    </div>
  );
}

// ─── Source-pool helpers ─────────────────────────────────────────────────────

function buildCandidatePool({ sourceType, sourceBrand, sourceMarkerBrand, collection }) {
  if (sourceType === 'collection') return collection;
  if (sourceType === 'sameBrand' && sourceMarkerBrand) {
    return allMarkers.filter((m) => m.brand === sourceMarkerBrand);
  }
  if (sourceType === 'specificBrand' && sourceBrand) {
    return allMarkers.filter((m) => m.brand === sourceBrand);
  }
  return allMarkers;
}

const SOURCE_OPTIONS = [
  { value: 'collection', label: 'My Collection' },
  { value: 'sameBrand', label: 'Same Brand' },
  { value: 'anyBrand', label: 'Any Brand' },
  { value: 'specificBrand', label: 'Specific Brand' },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ShadeFinderPage() {
  const location = useLocation();
  const { user } = useAuth();

  const [mode, setMode] = useState('single'); // 'single' | 'palette'

  // Single-marker controls
  const [inputBrand, setInputBrand] = useState('Ohuhu (New Code)');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMarker, setSelectedMarker] = useState(null);

  // Source pool (where to draw shades from)
  const [sourceType, setSourceType] = useState('collection');
  const [specificBrand, setSpecificBrand] = useState('Ohuhu');

  // Chain length is fixed at 2 (source + 2 shadows)
  const chainSteps = 2;

  // Palette mode controls
  const [paletteCode, setPaletteCode] = useState('');
  const [paletteCodeError, setPaletteCodeError] = useState('');
  const [palette, setPalette] = useState(null); // markers[] for palette mode
  const [paletteName, setPaletteName] = useState('');
  const [savedPalettes, setSavedPalettes] = useState([]);
  const [showSavedPicker, setShowSavedPicker] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);

  // Save modal
  const [saveModalPalette, setSaveModalPalette] = useState(null);

  const userCollection = useMemo(() => getUserCollection(), []);

  // Brand list for input search (single mode)
  const availableBrands = useMemo(() => {
    const baseBrands = BRANDS.filter((b) => b !== 'All' && b !== 'My Collection');
    const extended = [];
    baseBrands.forEach((brand) => {
      if (usesOldNewCodes(brand)) {
        extended.push(`${brand} (Old Code)`, `${brand} (New Code)`);
      } else {
        extended.push(brand);
      }
    });
    return extended;
  }, []);

  const brandOnlyList = useMemo(
    () => BRANDS.filter((b) => b !== 'All' && b !== 'My Collection'),
    []
  );

  const parseBrand = (brandString) => {
    if (brandString.endsWith(' (Old Code)')) {
      return { brand: brandString.slice(0, -' (Old Code)'.length), codeType: 'old' };
    }
    if (brandString.endsWith(' (New Code)')) {
      return { brand: brandString.slice(0, -' (New Code)'.length), codeType: 'new' };
    }
    return { brand: brandString, codeType: null };
  };

  // ─── Handle preloaded palette from navigation state ────────────────────────
  useEffect(() => {
    const state = location.state || {};
    if (state.palette && Array.isArray(state.palette) && state.palette.length > 0) {
      setMode('palette');
      setPalette(state.palette);
      setPaletteName(state.paletteName || '');
      if (state.paletteCode) setPaletteCode(state.paletteCode);
      // Clear navigation state so refresh doesn't re-trigger
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // ─── Single mode: compute chain ────────────────────────────────────────────
  const sourceClass = useMemo(
    () => (selectedMarker ? classifyShadeSource(selectedMarker.hex) : 'normal'),
    [selectedMarker]
  );

  const candidatePool = useMemo(() => {
    return buildCandidatePool({
      sourceType,
      sourceBrand: specificBrand,
      sourceMarkerBrand: selectedMarker?.brand,
      collection: userCollection,
    });
  }, [sourceType, specificBrand, selectedMarker, userCollection]);

  const singleChain = useMemo(() => {
    if (!selectedMarker) return [];
    if (sourceClass === 'extremeWhite' || sourceClass === 'extremeBlack') return [];
    return findShades(selectedMarker, candidatePool, chainSteps);
  }, [selectedMarker, candidatePool, chainSteps, sourceClass]);

  // ─── Palette mode: compute chain per source marker ─────────────────────────
  const paletteResults = useMemo(() => {
    if (!palette) return [];
    return palette.map((src) => {
      const klass = classifyShadeSource(src.hex);
      const pool =
        sourceType === 'sameBrand'
          ? allMarkers.filter((m) => m.brand === src.brand)
          : buildCandidatePool({
              sourceType,
              sourceBrand: specificBrand,
              sourceMarkerBrand: src.brand,
              collection: userCollection,
            });
      const chain =
        klass === 'extremeWhite' || klass === 'extremeBlack'
          ? []
          : findShades(src, pool, chainSteps);
      return { source: src, klass, chain };
    });
  }, [palette, sourceType, specificBrand, chainSteps, userCollection]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleSelectMarker = useCallback((m) => {
    setSelectedMarker(m);
    setSearchQuery('');
  }, []);

  const handleClearMarker = () => {
    setSelectedMarker(null);
    setSearchQuery('');
  };

  const handleLoadPaletteCode = () => {
    setPaletteCodeError('');
    if (!paletteCode.trim()) {
      setPaletteCodeError('Paste a palette share code first.');
      return;
    }
    try {
      const decoded = decodePaletteFromShare(paletteCode.trim());
      if (!decoded || decoded.length === 0) {
        setPaletteCodeError('That code didn\'t decode to any markers.');
        return;
      }
      setPalette(decoded);
      setPaletteName('');
    } catch (err) {
      setPaletteCodeError(err.message || 'Invalid palette code.');
    }
  };

  const handleClearPalette = () => {
    setPalette(null);
    setPaletteName('');
    setPaletteCode('');
    setPaletteCodeError('');
  };

  const openSavedPalettePicker = async () => {
    if (!user) {
      setPaletteCodeError('Sign in to load your saved palettes.');
      return;
    }
    setLoadingSaved(true);
    setShowSavedPicker(true);
    const result = await getUserPalettes(user.uid);
    setLoadingSaved(false);
    if (result.success) {
      setSavedPalettes(result.palettes);
    } else {
      setPaletteCodeError(result.error || 'Failed to load palettes.');
      setShowSavedPicker(false);
    }
  };

  const handleSelectSavedPalette = async (p) => {
    setShowSavedPicker(false);
    const result = await getPaletteById(user.uid, p.id);
    if (result.success && result.markers) {
      setPalette(result.markers);
      setPaletteName(p.name || '');
      setPaletteCode(p.code || '');
    } else {
      setPaletteCodeError(result.error || 'Failed to load that palette.');
    }
  };

  // Save only the markers at a given shadow depth (1 or 2) — single mode.
  // No source included. If a depth has no marker, save is a no-op.
  const handleSaveSingleShadowDepth = (depth) => {
    if (!selectedMarker || singleChain.length === 0) return;
    const step = singleChain[depth - 1];
    if (!step) return;
    setSaveModalPalette([step.marker]);
  };

  // Save only the markers at a given shadow depth (1 or 2) — palette mode.
  // Source markers are excluded. Sources whose chain doesn't reach that depth
  // are silently skipped, even if it makes the palette smaller.
  const handleSavePaletteShadowDepth = (depth) => {
    if (!paletteResults.length) return;
    const built = [];
    const seen = new Set();
    paletteResults.forEach(({ chain }) => {
      const step = chain[depth - 1];
      if (!step) return;
      const m = step.marker;
      const k = `${m.brand}|${m.hex}`;
      if (seen.has(k)) return;
      seen.add(k);
      built.push(m);
    });
    if (built.length === 0) return;
    setSaveModalPalette(built.slice(0, 20));
  };

  const singleHasD1 = singleChain.length >= 1;
  const singleHasD2 = singleChain.length >= 2;
  const paletteD1Count = paletteResults.filter((r) => r.chain[0]).length;
  const paletteD2Count = paletteResults.filter((r) => r.chain[1]).length;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-fuchsia-400 to-violet-500">
          Shades <Layers className="inline-block w-8 h-8 pb-1 text-violet-500" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          Find the perfect shading companions for your markers and build natural shadow progressions
        </p>
        <PageHelpModal
          title="How Shades works"
          tips={[
            { text: 'Pick a single marker or paste a palette share code to find shades for many markers at once.' },
            { text: '"My Collection" mode is the most useful — it tells you what you already own that can shade a given color.' },
            { text: 'Each shade swatch shows a quality badge (Excellent / Good / Approximate) based on how well the hue is preserved.' },
            { text: 'Save the resulting shaded palette to your collection just like any other palette.' },
          ]}
          trigger={
            <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
              Need help?
            </p>
          }
        />
      </div>

      {/* Mode toggle */}
      <div className="flex justify-center">
        <div className="inline-flex bg-white/80 backdrop-blur-sm border border-slate-200 rounded-2xl p-1 shadow-sm">
          {[
            { value: 'single', label: 'Single marker' },
            { value: 'palette', label: 'From palette' },
          ].map((m) => {
            const active = mode === m.value;
            return (
              <button
                key={m.value}
                onClick={() => setMode(m.value)}
                className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                  active
                    ? 'bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Source pool — shared by both modes */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
            Find shades from
          </label>
          <CustomSelect
            value={sourceType}
            onChange={setSourceType}
            options={SOURCE_OPTIONS.map((o) => o.value)}
            getLabel={(v) => SOURCE_OPTIONS.find((o) => o.value === v)?.label || v}
          />
        </div>

        {sourceType === 'specificBrand' && (
          <div className="space-y-1.5">
            <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
              Brand
            </label>
            <CustomSelect value={specificBrand} onChange={setSpecificBrand} options={brandOnlyList} />
          </div>
        )}
      </div>

      {/* "My Collection" empty notice */}
      {sourceType === 'collection' && userCollection.length === 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
          <Info size={14} className="text-amber-500 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700 leading-relaxed">
            Your collection is empty — switch to <strong>Same Brand</strong> or <strong>Any Brand</strong> mode, or add some markers to your collection first.
          </p>
        </div>
      )}

      {/* ─── Single mode UI ────────────────────────────────────────────────── */}
      {mode === 'single' && (
        <div className="space-y-6">
          <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                Marker brand
              </label>
              <CustomSelect value={inputBrand} onChange={setInputBrand} options={availableBrands} />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                Pick a marker
              </label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or code (e.g. BV000)"
                  className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          <MarkerSearch
            brand={parseBrand(inputBrand).brand}
            codeType={parseBrand(inputBrand).codeType}
            onSelect={handleSelectMarker}
            query={searchQuery}
            setQuery={setSearchQuery}
          />

          {/* Result panel */}
          {selectedMarker && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                  Shade chain
                </p>
                <button
                  onClick={handleClearMarker}
                  className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={13} /> Clear
                </button>
              </div>

              {/* Edge case messaging */}
              {sourceClass === 'extremeWhite' && (
                <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600 leading-relaxed">
                    This marker is essentially white — there's no shade chain to build. Try using it as a highlight instead.
                  </p>
                </div>
              )}
              {sourceClass === 'extremeBlack' && (
                <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600 leading-relaxed">
                    This marker is essentially black — use it as a shadow for lighter colors instead of looking for shades of it.
                  </p>
                </div>
              )}
              {sourceClass === 'tooDark' && singleChain.length === 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <Info size={14} className="text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 leading-relaxed">
                    This marker is already very dark — there's nothing meaningfully darker in the selected source pool.
                  </p>
                </div>
              )}
              {sourceClass === 'neutral' && singleChain.length > 0 && (
                <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-xl p-3">
                  <Info size={14} className="text-slate-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600 leading-relaxed">
                    This is a neutral / grey marker — shades are picked by lightness rather than hue.
                  </p>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
                {singleChain.length > 0 ? (
                  <ShadeChain source={selectedMarker} chain={singleChain} />
                ) : sourceClass === 'normal' || sourceClass === 'neutral' ? (
                  <div className="flex flex-col items-center text-center gap-3 py-4">
                    <ShadeSwatch marker={selectedMarker} role="Source" isSource />
                    <p className="text-xs text-slate-500 max-w-md">
                      No good shades found in the selected pool. Try switching the source — for example,{' '}
                      <button
                        onClick={() => setSourceType('anyBrand')}
                        className="text-violet-600 font-medium underline underline-offset-2"
                      >
                        Any Brand
                      </button>
                      .
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-2">
                    <ShadeSwatch marker={selectedMarker} role="Source" isSource />
                  </div>
                )}
              </div>

              {singleChain.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <p className="text-xs text-slate-400 mr-auto">
                    Save just one of the shadow layers as a palette.
                  </p>
                  {singleHasD1 && (
                    <button
                      onClick={() => handleSaveSingleShadowDepth(1)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-rose-pastel-400 to-pink-400 hover:from-rose-pastel-500 hover:to-pink-500 text-white text-xs font-semibold shadow-md transition-all"
                    >
                      <Heart size={14} />
                      Save Shadow 1
                    </button>
                  )}
                  {singleHasD2 && (
                    <button
                      onClick={() => handleSaveSingleShadowDepth(2)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white text-xs font-semibold shadow-md transition-all"
                    >
                      <Heart size={14} />
                      Save Shadow 2
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Palette mode UI ──────────────────────────────────────────────── */}
      {mode === 'palette' && (
        <div className="space-y-6">
          {!palette ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400">
                  Palette share code
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={paletteCode}
                    onChange={(e) => {
                      setPaletteCode(e.target.value);
                      setPaletteCodeError('');
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleLoadPaletteCode();
                    }}
                    placeholder="PC2-..."
                    className="flex-1 px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent transition-all font-mono"
                  />
                  <button
                    onClick={handleLoadPaletteCode}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white text-xs font-semibold shadow transition-all"
                  >
                    Load
                  </button>
                </div>
                {paletteCodeError && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle size={12} /> {paletteCodeError}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">or</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              <button
                onClick={openSavedPalettePicker}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold transition-all"
              >
                <Sparkles size={14} className="text-violet-500" />
                {user ? 'Pick from your saved palettes' : 'Sign in to use your saved palettes'}
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Header strip — title (left), close (right), color bar below */}
              <div>
                <div className="flex items-center justify-between mb-2 gap-3">
                  <p className="text-sm font-semibold text-slate-700 truncate">
                    {paletteName || 'Loaded palette'}
                  </p>
                  <button
                    onClick={handleClearPalette}
                    className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors shrink-0"
                    title="Change palette"
                  >
                    <X size={14} />
                  </button>
                </div>
                <div className="flex h-12 rounded-xl overflow-hidden">
                  {palette.map((m, i) => (
                    <div
                      key={i}
                      className="flex-1"
                      style={{ backgroundColor: m.hex }}
                      title={m.name}
                    />
                  ))}
                </div>
              </div>

              {/* Per-marker chains as a grid of rows */}
              <div className="space-y-3">
                {paletteResults.map((row, idx) => (
                  <div
                    key={markerKey(row.source, idx)}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                  >
                    {row.chain.length > 0 ? (
                      <ShadeChain source={row.source} chain={row.chain} compact />
                    ) : (
                      <div className="flex items-center gap-3">
                        <ShadeSwatch marker={row.source} role="Source" isSource sizeClass="w-14 h-14" />
                        <p className="text-xs text-slate-400 italic">
                          {row.klass === 'extremeWhite' && 'White — no shade needed.'}
                          {row.klass === 'extremeBlack' && 'Black — use as a shadow itself.'}
                          {row.klass === 'tooDark' && 'Already very dark — no useful shade in this pool.'}
                          {(row.klass === 'normal' || row.klass === 'neutral') &&
                            'No good shade found in the selected pool.'}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Save action — pick a single shadow depth, no source */}
              {(paletteD1Count > 0 || paletteD2Count > 0) && (
                <div className="flex flex-wrap items-center gap-2 justify-end">
                  <p className="text-xs text-slate-400 mr-auto">
                    Save just one shadow layer as a palette (sources excluded; markers without that depth are skipped).
                  </p>
                  {paletteD1Count > 0 && (
                    <button
                      onClick={() => handleSavePaletteShadowDepth(1)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-rose-pastel-400 to-pink-400 hover:from-rose-pastel-500 hover:to-pink-500 text-white text-xs font-semibold shadow-md transition-all"
                    >
                      <Heart size={14} />
                      Save Shadow 1 ({Math.min(paletteD1Count, 20)})
                    </button>
                  )}
                  {paletteD2Count > 0 && (
                    <button
                      onClick={() => handleSavePaletteShadowDepth(2)}
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 hover:from-fuchsia-400 hover:to-violet-400 text-white text-xs font-semibold shadow-md transition-all"
                    >
                      <Heart size={14} />
                      Save Shadow 2 ({Math.min(paletteD2Count, 20)})
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Saved palette picker modal */}
      {showSavedPicker && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeOnly">
          <div
            className="absolute inset-0"
            onClick={() => setShowSavedPicker(false)}
          />
          <div className="relative bg-white/95 backdrop-blur-md rounded-2xl max-w-md w-full p-6 shadow-soft-xl border border-rose-pastel-100/50 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">Pick a saved palette</h3>
              <button
                onClick={() => setShowSavedPicker(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
            {loadingSaved ? (
              <div className="flex justify-center py-10">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-violet-500 rounded-full animate-spin" />
              </div>
            ) : savedPalettes.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">
                No saved palettes yet — create one first from the home page.
              </p>
            ) : (
              <div className="overflow-y-auto space-y-2 flex-1">
                {savedPalettes.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handleSelectSavedPalette(p)}
                    className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="flex h-10 w-20 rounded-lg overflow-hidden shrink-0 border border-slate-100">
                      {(p.previewColors || []).map((hex, i) => (
                        <div key={i} className="flex-1" style={{ backgroundColor: hex }} />
                      ))}
                      {(p.previewColors || []).length === 0 && <div className="flex-1 bg-slate-100" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                      <p className="text-[11px] text-slate-400">
                        {p.colorCount || (p.previewColors || []).length} colors
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save palette modal — reuse existing component */}
      {saveModalPalette && (
        <SavePaletteModal
          isOpen={true}
          palette={saveModalPalette}
          onClose={() => setSaveModalPalette(null)}
        />
      )}
    </div>
  );
}
